import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmButton } from "@/components/ConfirmButton";
import { EmployeeFormDialog } from "@/components/EmployeeFormDialog";
import { InlineEmailCell } from "@/components/InlineEmailCell";
import { ImportEmailsButton } from "@/components/ImportEmailsButton";
import { EmployeeSheet } from "@/components/EmployeeSheet";
import { api, formatApiError, formatDate } from "@/lib/api";

const Row = ({ e, onEdit, onToggle, onDelete, onEmail, onOpen, canDelete }) => (
  <TableRow data-testid={`employee-row-${e.id}`} className="hover:bg-slate-50">
    <TableCell>
      <button type="button" onClick={() => onOpen(e)} className="text-left font-medium text-slate-900 hover:underline" data-testid={`open-employee-${e.id}`}>{e.cognome} {e.nome}</button>
      {e.matricola && <p className="text-xs text-slate-500">Matr. {e.matricola}</p>}
    </TableCell>
    <TableCell className="font-mono text-xs">{e.codice_fiscale || <span className="text-slate-400">—</span>}</TableCell>
    <TableCell className="text-sm">{e.sede || <span className="text-slate-400">—</span>}<p className="text-xs text-slate-500">{e.mansione}</p></TableCell>
    <TableCell className="text-sm">{e.tipo_contratto || <span className="text-slate-400">—</span>}<p className="text-xs text-slate-500">{[e.livello, e.ore_settimanali != null && e.ore_settimanali !== "" ? `${e.ore_settimanali} h` : ""].filter(Boolean).join(" · ")}</p></TableCell>
    <TableCell><InlineEmailCell employee={e} onSave={onEmail} /></TableCell>
    <TableCell className="text-sm text-slate-600">{e.data_assunzione ? formatDate(e.data_assunzione, false) : "—"}</TableCell>
    <TableCell>
      <div className="flex items-center gap-2">
        <Switch checked={e.attivo} onCheckedChange={(v) => onToggle(e, v)} data-testid={`employee-status-switch-${e.id}`} />
        <span className={`text-xs ${e.attivo ? "text-emerald-700" : "text-rose-700"}`}>{e.attivo ? "Attivo" : "Cessato"}</span>
      </div>
    </TableCell>
    <TableCell className="text-right">
      <div className="flex justify-end gap-1">
        <Button size="icon" variant="ghost" onClick={() => onEdit(e)} data-testid={`edit-employee-${e.id}`}><Pencil className="h-4 w-4" /></Button>
        {canDelete && <ConfirmButton testId={`delete-employee-${e.id}`} size="icon" title={`Eliminare ${e.cognome} ${e.nome}?`}
          description="Per chi si è dimesso puoi anche solo segnarlo come cessato, mantenendo lo storico." onConfirm={() => onDelete(e)}>
          <Trash2 className="h-4 w-4" />
        </ConfirmButton>}
      </div>
    </TableCell>
  </TableRow>
);

export default function Employees() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [sede, setSede] = useState("tutte");
  const [sedi, setSedi] = useState([]);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("attivi");
  const [dialog, setDialog] = useState({ open: false, employee: null });
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/employees", { params: { q, stato, sede: sede === "tutte" ? "" : sede } }).then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [q, stato, sede]);
  useEffect(() => { api.get("/settings").then((r) => setSedi(r.data.sedi || [])).catch(() => {}); }, []);
  const exportCsv = async () => {
    try {
      const res = await api.get("/employees/export.csv", { params: { q, stato, sede: sede === "tutte" ? "" : sede }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `dipendenti_${new Date().toISOString().slice(0, 10)}.csv` });
      a.click(); setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggle = async (e, attivo) => {
    const { id, created_at, ...body } = e;
    const today = new Date().toISOString().slice(0, 10);
    try {
      await api.put(`/employees/${id}`, { ...body, attivo, data_cessazione: attivo ? "" : body.data_cessazione || today });
      toast.success(attivo ? "Dipendente riattivato" : "Dipendente segnato come cessato");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const saveEmail = async (e, email) => {
    const { id, created_at, ...body } = e;
    try {
      const { data } = await api.put(`/employees/${id}`, { ...body, email });
      setList((l) => l.map((x) => (x.id === id ? data : x)));
      toast.success(`Email salvata per ${e.cognome} ${e.nome}`);
      return true;
    } catch (err) { toast.error(formatApiError(err)); return false; }
  };
  const remove = async (e) => {
    try { await api.delete(`/employees/${e.id}`); toast.success("Dipendente eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      <PageHeader eyebrow="Anagrafica" title="Dipendenti"
        description="Il personale dell'attività: anagrafica, sede, mansione e contratto. Segna come cessati chi lascia."
        actions={<div className="flex flex-wrap gap-2"><Button variant="outline" onClick={exportCsv} data-testid="export-employees-csv-button"><Download className="mr-2 h-4 w-4" /> Esporta CSV</Button><ImportEmailsButton onDone={load} /><Button onClick={() => setDialog({ open: true, employee: null })} className="active:scale-[0.98]" data-testid="add-employee-button"><Plus className="mr-2 h-4 w-4" /> Nuovo dipendente</Button></div>} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, codice fiscale o email" className="pl-9" data-testid="employee-search-input" />
        </div>
        {sedi.length > 0 && <Select value={sede} onValueChange={setSede}><SelectTrigger className="sm:w-48" data-testid="employee-sede-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="tutte">Tutte le sedi</SelectItem>{sedi.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>}
        <Tabs value={stato} onValueChange={setStato}>
          <TabsList>
            <TabsTrigger value="attivi" data-testid="employee-filter-attivi">Attivi</TabsTrigger>
            <TabsTrigger value="cessati" data-testid="employee-filter-cessati">Cessati</TabsTrigger>
            <TabsTrigger value="tutti" data-testid="employee-filter-tutti">Tutti</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="panel overflow-x-auto" data-testid="employees-table">
        <Table className="min-w-[1000px]">
          <TableHeader>
            <TableRow>
              <TableHead>Dipendente</TableHead><TableHead>Codice fiscale</TableHead><TableHead>Sede / mansione</TableHead><TableHead>Contratto</TableHead><TableHead>Email</TableHead>
              <TableHead>Assunzione</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((e) => <Row key={e.id} e={e} onEdit={(emp) => setDialog({ open: true, employee: emp })} onToggle={toggle} onDelete={remove} onEmail={saveEmail} onOpen={(emp) => setOpenId(emp.id)} canDelete={user?.role === "admin"} />)}
            {list.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-slate-500" data-testid="employees-empty">Nessun dipendente trovato.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <EmployeeFormDialog open={dialog.open} employee={dialog.employee} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={load} />
      <EmployeeSheet employeeId={openId} onClose={() => setOpenId(null)} />
    </>
  );
}
